import { AxisNode, ReportGridModel } from './model';
import { formatDecimal } from './decimal';
import { matchesNumeric } from './search';
import { dimensionLabel } from './dimensionLabel';
import { ReportGridDataset, ReportGridProps } from '../types';

export interface DisplayCell { value: string | null; text: string }
export interface ReportGridPresentation {
  balance?: { initial: DisplayCell; cell: (column: AxisNode) => DisplayCell };
  label: (node: AxisNode, axis: 'rows' | 'columns') => string;
  cell: (row: AxisNode, column: AxisNode) => DisplayCell;
  search: (query: string) => { rows: Set<string>; cells: Set<DisplayCell> };
}
export const normalizeSearch = (value: string, locale: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase(locale).trim();

/** Dataset-scoped display/search index. Repeated amounts (especially zero) share one entry. */
export function buildPresentation(model: ReportGridModel, dataset: ReportGridDataset, locale: string,
  formatValue: ReportGridProps['formatValue'], formatDimension: ReportGridProps['formatDimension'],
  missing: string, nullDimension: string, grandTotal: string): ReportGridPresentation {
  const labels = { rows: new Map<string, string>(), columns: new Map<string, string>() };
  const rowNames = new Map<string, string>();
  for (const axis of ['rows', 'columns'] as const) {
    const dimensions = axis === 'rows' ? dataset.rowDimensions : dataset.columnDimensions;
    const nodes = axis === 'rows' ? model.rowNodes : model.columnNodes;
    labels[axis].set('[]', grandTotal);
    nodes.forEach(node => {
      const value = node.path[node.depth - 1];
      const label = formatDimension ? formatDimension(value, dimensions[node.depth - 1]) : value === null ? nullDimension : dimensionLabel(value, dimensions[node.depth - 1]);
      labels[axis].set(node.id, label);
      if (axis === 'rows') rowNames.set(node.id, normalizeSearch(label, locale));
    });
  }
  const uniqueValues = new Map<string | null, DisplayCell>();
  const display = (value: string | null): DisplayCell => {
    let cell = uniqueValues.get(value);
    if (!cell) {
      cell = { value, text: formatValue ? formatValue(value, dataset.measure) : value === null ? missing : formatDecimal(value, dataset.measure.decimalPlaces, locale, dataset.measure.currency) };
      uniqueValues.set(value, cell);
    }
    return cell;
  };
  const matrix = new Map<string, Map<string, DisplayCell>>();
  const rowValues = new Map<string, Set<DisplayCell>>();
  const columns = [...model.columnNodes, model.columns];
  for (const row of [...model.rowNodes, model.rows]) {
    const byColumn = new Map<string, DisplayCell>();
    const values = new Set<DisplayCell>();
    for (const column of columns) {
      const value = model.getValue(row, column);
      const cell = display(value);
      byColumn.set(column.id, cell);
      if (value !== null) values.add(cell);
    }
    matrix.set(row.id, byColumn);
    rowValues.set(row.id, values);
  }
  const balanceCells = new Map<string, DisplayCell>();
  if (model.balance) columns.forEach(column => balanceCells.set(column.id, display(model.balance!.getClosing(column))));
  return {
    balance: model.balance ? { initial: display(model.balance.initialValue), cell: column => balanceCells.get(column.id)! } : undefined,
    label: (node, axis) => labels[axis].get(node.id)!,
    cell: (row, column) => matrix.get(row.id)!.get(column.id)!,
    search: query => {
      const term = normalizeSearch(query, locale);
      const matchingCells = new Set<DisplayCell>();
      const matchingRows = new Set<string>();
      if (!term) return { rows: matchingRows, cells: matchingCells };
      if (/\d/.test(query)) uniqueValues.forEach(cell => {
        if (cell.value !== null && matchesNumeric(cell.value, cell.text, query)) matchingCells.add(cell);
      });
      model.rowNodes.forEach(row => {
        if (rowNames.get(row.id)!.includes(term) || [...rowValues.get(row.id)!].some(cell => matchingCells.has(cell))) matchingRows.add(row.id);
      });
      return { rows: matchingRows, cells: matchingCells };
    },
  };
}
